/**
 * Motor central de estoque ficticio por regiao.
 *
 * Responsabilidades:
 * - Persistir regiao selecionada pelo cliente (CEP ou botao)
 * - Resolver regiao por regras locais de CEP
 * - Expor API unica para limites por variante
 * - Emitir evento global quando a regiao muda
 */
(function () {
  const STORAGE_KEY = 'jv_region_state_v1';
  const CHANGE_EVENT = 'jv:region-changed';

  /** @type {{enabled:boolean,simplified:boolean,regions:Array<any>}|null} */
  let config = null;

  /** @type {{code:string|null,label:string|null,source:string|null,cep:string|null,updatedAt:number|null}} */
  let state = {
    code: null,
    label: null,
    source: null,
    cep: null,
    updatedAt: null,
  };

  const safeJsonParse = (raw, fallback) => {
    if (!raw) return fallback;

    try {
      return JSON.parse(raw);
    } catch (_) {
      return fallback;
    }
  };

  const normalizeCode = (value) => String(value || '').trim().toLowerCase();

  const normalizeCep = (value) => String(value || '').replace(/\D/g, '');

  /**
   * Uma linha pode ser:
   * - Grupo por prefixo: `12214` ou `12214*` (qualquer CEP que comece com esses digitos, ex. 12214-000 … 12214-999)
   * - CEP exato mascarado: `12243-000` (8 digitos apos normalizar)
   * - Intervalo numerico: `12214000-12214999` ou `12000000-12999999` (dois blocos de 8 digitos)
   * Separadores entre regras: virgula, ponto-e-virgula ou quebra de linha.
   */
  const parseCepRanges = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return [];

    const ranges = [];
    const parts = raw.split(/[\n,;]+/);

    for (const partRaw of parts) {
      const part = partRaw.trim();
      if (!part) continue;

      if (part.includes('*')) {
        const prefix = normalizeCep(part.replace(/\*/g, ''));
        if (!prefix) continue;
        ranges.push({ type: 'prefix', prefix, length: prefix.length });
        continue;
      }

      if (part.includes('-')) {
        const pieces = part.split('-');
        if (pieces.length === 2) {
          const start = normalizeCep(pieces[0]);
          const end = normalizeCep(pieces[1]);
          const merged = start + end;

          if (merged.length === 8 && start.length === 5 && end.length === 3) {
            ranges.push({ type: 'exact', value: merged, length: 8 });
            continue;
          }

          if (start.length === 8 && end.length === 8) {
            ranges.push({ type: 'range', start, end, length: 8 });
            continue;
          }

          if (start && end) {
            ranges.push({ type: 'range', start, end, length: Math.max(start.length, end.length) });
            continue;
          }
        }
        continue;
      }

      const token = normalizeCep(part);
      if (!token) continue;
      ranges.push({ type: 'prefix', prefix: token, length: token.length });
    }

    return ranges;
  };

  const matchesCepRule = (cep, rule) => {
    if (!cep || !rule) return false;

    if (rule.type === 'exact') {
      return cep === rule.value;
    }

    if (rule.type === 'prefix') {
      return cep.startsWith(rule.prefix);
    }

    if (rule.type === 'range') {
      const len = rule.length || 8;
      const cepNum = Number(cep.slice(0, len));
      const startNum = Number(rule.start.slice(0, len));
      const endNum = Number(rule.end.slice(0, len));

      if (Number.isNaN(cepNum) || Number.isNaN(startNum) || Number.isNaN(endNum)) return false;
      return cepNum >= Math.min(startNum, endNum) && cepNum <= Math.max(startNum, endNum);
    }

    return false;
  };

  const resolveRegionByCep = (cepRaw) => {
    if (!config || !Array.isArray(config.regions)) return null;

    const cep = normalizeCep(cepRaw);
    if (!cep) return null;

    /** @type {Array<{region:any,rule:any}>} */
    const candidateRules = [];

    for (const region of config.regions) {
      if (!Array.isArray(region.cepRules) || region.cepRules.length === 0) continue;
      for (const rule of region.cepRules) {
        candidateRules.push({ region, rule });
      }
    }

    // Priorizamos regras mais especificas (maior comprimento) para evitar colisao de prefixos.
    candidateRules.sort((a, b) => (b.rule.length || 0) - (a.rule.length || 0));

    for (const candidate of candidateRules) {
      if (matchesCepRule(cep, candidate.rule)) return candidate.region;
    }

    return null;
  };

  const persistState = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {}
  };

  const emitChange = () => {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { ...state } }));
  };

  const setState = ({ code, label, source, cep }) => {
    state = {
      code: code || null,
      label: label || null,
      source: source || null,
      cep: cep || null,
      updatedAt: Date.now(),
    };

    persistState();
    emitChange();
  };

  const hydrateState = () => {
    const saved = safeJsonParse(localStorage.getItem(STORAGE_KEY), null);
    if (!saved || typeof saved !== 'object') return;

    state = {
      code: saved.code || null,
      label: saved.label || null,
      source: saved.source || null,
      cep: saved.cep || null,
      updatedAt: saved.updatedAt || null,
    };
  };

  const getRegionalStocksFromElement = (element) => {
    if (!(element instanceof HTMLElement)) return null;

    return safeJsonParse(element.dataset.jvRegionalStocks || '', null);
  };

  const getRegionalStockForVariant = (variantId, element) => {
    const stocks = getRegionalStocksFromElement(element);
    if (!stocks || typeof stocks !== 'object') return null;

    const variantValue = stocks[String(variantId)];
    if (variantValue == null || variantValue === '') return 0;

    const selectedRegion = config?.regions?.find((region) => region.code === normalizeCode(state.code));
    const selectedRegionStock = selectedRegion && selectedRegion.stock != null && !Number.isNaN(selectedRegion.stock)
      ? Math.max(0, parseInt(selectedRegion.stock, 10))
      : null;
    const hasRegionCap = selectedRegionStock != null && selectedRegionStock > 0;

    // Suporte a formato simples: inteiro unico por produto/variante.
    if (typeof variantValue === 'number' || typeof variantValue === 'string') {
      const directQty = parseInt(variantValue, 10);
      if (Number.isNaN(directQty)) return 0;
      const totalStock = Math.max(0, directQty);
      return hasRegionCap ? Math.min(totalStock, selectedRegionStock) : totalStock;
    }

    if (typeof variantValue !== 'object') return 0;

    const totalRaw = variantValue.total;
    const totalQtyParsed = parseInt(totalRaw, 10);
    const totalQty = Number.isNaN(totalQtyParsed) ? 0 : Math.max(0, totalQtyParsed);

    const regionsMap = variantValue.regions && typeof variantValue.regions === 'object' ? variantValue.regions : null;
    const code = normalizeCode(state.code);
    const regionalRaw = code && regionsMap ? regionsMap[code] : null;
    const regionalQtyParsed = parseInt(regionalRaw, 10);
    const regionalQty = Number.isNaN(regionalQtyParsed) ? null : Math.max(0, regionalQtyParsed);

    // Regra final:
    // 1) Base total ficticio
    // 2) Se houver regiao selecionada e metacampo regional, limita por ele
    // 3) Se houver cap manual de teste no bloco, ele sobrepoe o regional (mais restritivo)
    let effective = totalQty;

    if (code && regionalQty != null) {
      effective = Math.min(effective, regionalQty);
    }

    if (hasRegionCap) {
      effective = Math.min(effective, selectedRegionStock);
    }

    return Math.max(0, effective);
  };

  const getEffectiveMax = ({ variantId, baseMax, element, cartQty }) => {
    const regionalStock = getRegionalStockForVariant(variantId, element);
    const hasBase = baseMax != null && !Number.isNaN(baseMax);
    const hasRegional = regionalStock != null && !Number.isNaN(regionalStock);

    if (!hasBase && !hasRegional) return null;
    if (hasBase && !hasRegional) return baseMax;
    /** Só regional: `regionalStock` é o teto total da variante na região — precisa subtrair o carrinho (igual ao ramo combined). */
    if (!hasBase && hasRegional) {
      if (cartQty == null || Number.isNaN(cartQty)) return regionalStock;
      return Math.max(0, regionalStock - cartQty);
    }

    const combined = Math.min(baseMax, regionalStock);
    if (cartQty == null || Number.isNaN(cartQty)) return combined;
    return Math.max(0, combined - cartQty);
  };

  const normalizeConfig = (incoming) => {
    const rawRegions = Array.isArray(incoming?.regions) ? incoming.regions : [];
    const regions = rawRegions.map((region) => {
      const code = normalizeCode(region.code || region.name);
      return {
        code,
        label: String(region.name || code || '').trim(),
        stock: region.stock == null || region.stock === '' ? null : parseInt(region.stock, 10),
        cepRules: parseCepRanges(region.cep_ranges),
      };
    }).filter((region) => region.code);

    return {
      enabled: !!incoming?.enabled,
      simplified: !!incoming?.simplified,
      regions,
    };
  };

  const init = (incomingConfig) => {
    config = normalizeConfig(incomingConfig || {});

    hydrateState();

    // Limpa estado invalido quando regiao removida do schema.
    if (state.code) {
      const exists = config.regions.some((region) => region.code === normalizeCode(state.code));
      if (!exists) {
        setState({ code: null, label: null, source: null, cep: null });
      }
    }

    emitChange();
  };

  const setRegionByCode = (codeRaw, source = 'manual') => {
    const code = normalizeCode(codeRaw);
    if (!config || !code) return false;

    const region = config.regions.find((item) => item.code === code);
    if (!region) return false;

    setState({
      code: region.code,
      label: region.label,
      source,
      cep: state.cep,
    });
    return true;
  };

  const setRegionByCep = (cepRaw) => {
    const cep = normalizeCep(cepRaw);
    const region = resolveRegionByCep(cep);
    if (!region) return null;

    setState({
      code: region.code,
      label: region.label,
      source: 'cep',
      cep,
    });
    return region;
  };

  const clearRegion = () => {
    setState({ code: null, label: null, source: null, cep: null });
  };

  window.JVRegionalStock = {
    CHANGE_EVENT,
    init,
    getState: () => ({ ...state }),
    getConfig: () => (config ? { ...config } : null),
    setRegionByCode,
    setRegionByCep,
    clearRegion,
    getEffectiveMax,
    getRegionalStockForVariant,
    normalizeCode,
  };
})();
